import { useCallback, useMemo } from 'react'
import { AccessToken, useAccessToken } from '../../../Hooks/useAccessToken'
import { StatusCodes } from '../../../../NonReact/constants/StatusCodes'
import { APICallback, APICallbackError, MountOptionsOverrides } from './Types'

/**
 * This hook is responsible for providing the necessary authentication token and error handling callbacks for the CX Score external component.
 */
export const useCXScore = (): MountOptionsOverrides => {
    const { accessToken, renewToken }: AccessToken = useAccessToken()

    /**
     * Callback function to handle 4xx API errors.
     * If the error status is UNAUTHORISED, it will attempt to renew the access token.
     */
    const onAPI4xx: APICallback = useCallback<APICallback>(
        ({ status }: APICallbackError): void => {
            if (status === StatusCodes.UNAUTHORISED) {
                renewToken()
            }
        },
        [renewToken]
    )

    /**
     * TODO: I would like to replace this mechanism with a more generic solution, probably using a guard component
     * I don't like passing the callbacks to core application functions into a sub component like this
     * as it creates tight coupling between the external component and the core application, which makes it less reusable and harder to maintain.
     */
    return useMemo(
        (): MountOptionsOverrides => ({
            authToken: accessToken,
            onError: renewToken,
            callbacks: {
                onAPI4xx
            }
        }),
        [accessToken, renewToken, onAPI4xx]
    )
}
